import { Inbox as InboxIcon } from '@mui/icons-material';
import { Badge, BadgeProps, Box, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../hooks/use-event-bus';
import { usePubnub } from '../../../../hooks/use-pubnub/use-pubnub';
import { useSlideout } from '../../../../hooks/use-slideout';
import InboxService from '../../../../services/inbox-service/inbox-service';

const Container = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  paddingRight: '20px'
});

const ActionMenu = styled(IconButton)(({ theme }) => ({
  width: 48,
  height: 48,
  margin: '11px 0 11px 0',
  backgroundColor: '#004763',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#265D7C'
  },
  '&:focus-visible': {
    border: '2px solid #1EB9FB'
  },
  '&.open': {
    backgroundColor: '#0675DD',
    '&:hover': {
      backgroundColor: '#0675DD'
    }
  }
}));

const InboxBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    marginTop: 6,
    marginRight: -6,
    backgroundColor: '#1EB9FB',
    color: '#032F40'
  }
}));

export default function InboxButton() {
  const config = useConfig();
  const { isVisibleInboxPreview } = useSlideout();
  const { publish } = useEventBus();
  const { mailboxes } = usePubnub();
  const { selectedLocation } = useLocations();
  const { oidcService } = useOidcWithAdmin();
  const [unreadCount, setUnreadCount] = useState(0);
  const { listen } = useNotifications();

  const showInbox = useCallback(() => {
    if (!isVisibleInboxPreview)
      publish({
        messageType: 'inboxPreview'
      });
  }, [publish, isVisibleInboxPreview]);

  const inboxService = useMemo(() => InboxService.getInstance(config, oidcService), [config, oidcService]);

  const fetchUnread = useCallback(async () => {
    if (!mailboxes.length) return;

    const locationMailboxes = mailboxes.filter((d) => d.locationId === selectedLocation?.id);
    const { total } = await inboxService.channelsUnreadCounts({
      mailboxIds: locationMailboxes.map((d) => d.id)
    });

    setUnreadCount(total);
  }, [setUnreadCount, inboxService, mailboxes, selectedLocation]);

  useEffect(() => {
    fetchUnread();
    return listen('channel-update', () => {
      fetchUnread();
    });
  }, [listen, fetchUnread]);

  return (
    <Container>
      <InboxBadge badgeContent={unreadCount} showZero={false} overlap="circular">
        <ActionMenu className={isVisibleInboxPreview ? 'open' : ''} onClick={showInbox}>
          <InboxIcon />
        </ActionMenu>
      </InboxBadge>
    </Container>
  );
}
